import { Controller } from "stimulus"
import { showToast } from "../utils/toast"

type AddressDelta = { add: string[]; remove: string[] }

type Payload = {
  bot_ids?: string[]
  // Bulk rows span every campaign the user owns, not just the paginated page.
  all_bots?: boolean
  enabled?: boolean
  manager_ids?: string[]
  forward_to_addresses?: string[]
  // "For all bots" adds and removes rather than overwriting, so an address that only ever lived
  // on one campaign survives a bulk change.
  forward_to_addresses_delta?: AddressDelta
  // interview invite recipients follow forward-to: a row sets its own list, "for all bots" a delta
  invite_recipients?: string[]
  invite_recipients_delta?: AddressDelta
}

type MasterState = "all" | "none" | "mixed"

type SaveResponse = { success: boolean; message?: string; master_state?: MasterState }

export default class extends Controller {
  static targets = [
    "masterToggle", "bulkManagers", "bulkForwards", "bulkInvites",
    "row", "rowToggle", "rowManagers", "rowForwards", "rowInvites", "rowStatus"
  ]
  static values = {
    url: String,
    owner: String,
    // Set when the page is filtered to a whole company: bulk then reaches campaigns owned by
    // several different people, so it asks first.
    confirmBulk: Boolean,
    scopeLabel: String,
    scopeCount: Number
  }

  readonly urlValue!: string
  readonly ownerValue!: string
  readonly confirmBulkValue!: boolean
  readonly scopeLabelValue!: string
  readonly scopeCountValue!: number
  readonly rowTargets!: HTMLElement[]
  readonly masterToggleTargets!: HTMLInputElement[]

  connect(): void {
    this.masterToggleTargets.forEach(master => {
      master.indeterminate = master.dataset.indeterminate === "true"
    })
  }

  toggleRow(event: Event): void {
    const toggle = event.target as HTMLInputElement
    const row = toggle.closest<HTMLElement>("[data-bot-id]")!
    const enabled = toggle.checked

    this.submit({ bot_ids: [row.dataset.botId!], enabled }, toggle)
      .then(data => {
        this.setRowManagersDisabled(row, !enabled)
        this.updateRowStatus(row)
        this.applyMasterState(data)
      })
      .catch(() => { toggle.checked = !enabled })
  }

  saveRowManagers(event: Event): void {
    const select = event.target as HTMLSelectElement
    const row = select.closest<HTMLElement>("[data-bot-id]")!

    this.submit({ bot_ids: [row.dataset.botId!], manager_ids: this.selectedValues(select) }, select)
      .then(() => { this.updateRowStatus(row) })
      .catch(() => { /* the toast already reported it */ })
  }

  toggleAll(event: Event): void {
    const toggle = event.target as HTMLInputElement
    const enabled = toggle.checked

    if (!this.confirmBulk(enabled ? "Turn inbox management on" : "Turn inbox management off")) {
      toggle.checked = !enabled
      return
    }

    this.submit({ all_bots: true, enabled }, toggle)
      .then(data => {
        this.masterToggleTargets.forEach(master => {
          master.checked = enabled
          master.indeterminate = false
        })
        this.rowTargets.forEach(row => {
          this.rowToggle(row).checked = enabled
          this.setRowManagersDisabled(row, !enabled)
          this.updateRowStatus(row)
        })
        this.applyMasterState(data)
      })
      .catch(() => {
        toggle.checked = !enabled
        toggle.indeterminate = toggle.dataset.indeterminate === "true"
      })
  }

  assignAll(event: Event): void {
    const select = event.target as HTMLSelectElement
    const chosen = Array.from(select.selectedOptions)

    if (!this.confirmBulk("Set this manager")) {
      this.clearSelection(select)
      return
    }

    this.submit({ all_bots: true, manager_ids: chosen.map(option => option.value) }, select)
      .then(() => {
        this.rowTargets.forEach(row => {
          const rowSelect = this.rowManagers(row)
          rowSelect.innerHTML = ""
          chosen.forEach(option => {
            const copy = option.cloneNode(true) as HTMLOptionElement
            copy.selected = true
            rowSelect.appendChild(copy)
          })
          // the picker paints itself from the <select>, so tell it to re-read
          rowSelect.dispatchEvent(new CustomEvent("manager-picker:sync", { bubbles: true }))
          this.updateRowStatus(row)
        })
      })
      .catch(() => { /* the toast already reported it */ })
  }

  saveRowForwardAddresses(event: Event): void {
    const select = event.target as HTMLSelectElement
    const row = select.closest<HTMLElement>("[data-bot-id]")!
    const addresses = this.selectedValues(select)

    this.submit({ bot_ids: [row.dataset.botId!], forward_to_addresses: addresses }, select)
      .then(() => { this.storeAddresses(select, addresses) })
      .catch(() => { this.restoreAddresses(select) })
  }

  saveAllForwardAddresses(event: Event): void {
    const select = event.target as HTMLSelectElement
    const previous = this.storedAddresses(select)
    const current = this.selectedValues(select)
    const add = current.filter(address => !previous.includes(address))
    const remove = previous.filter(address => !current.includes(address))
    if (add.length === 0 && remove.length === 0) return

    if (!this.confirmBulk("Change the forward-to addresses")) {
      this.restoreAddresses(select)
      return
    }

    this.submit({ all_bots: true, forward_to_addresses_delta: { add, remove } }, select)
      .then(() => {
        this.storeAddresses(select, current)
        // Rows on other pages are correct on their next load; these are the ones on screen.
        this.rowTargets.forEach(row => { this.applyAddressDelta(row, add, remove) })
      })
      .catch(() => { this.restoreAddresses(select) })
  }

  saveRowInviteRecipients(event: Event): void {
    const select = event.target as HTMLSelectElement
    const row = select.closest<HTMLElement>("[data-bot-id]")!
    const addresses = this.selectedValues(select)

    this.submit({ bot_ids: [row.dataset.botId!], invite_recipients: addresses }, select)
      .then(() => { this.storeAddresses(select, addresses) })
      .catch(() => { this.restoreAddresses(select) })
  }

  saveAllInviteRecipients(event: Event): void {
    const select = event.target as HTMLSelectElement
    const previous = this.storedAddresses(select)
    const current = this.selectedValues(select)
    const add = current.filter(address => !previous.includes(address))
    const remove = previous.filter(address => !current.includes(address))
    if (add.length === 0 && remove.length === 0) return

    if (!this.confirmBulk("Change the interview invite recipients")) {
      this.restoreAddresses(select)
      return
    }

    this.submit({ all_bots: true, invite_recipients_delta: { add, remove } }, select)
      .then(() => {
        this.storeAddresses(select, current)
        this.rowTargets.forEach(row => { this.applyAddressDelta(row, add, remove, "rowInvites") })
      })
      .catch(() => { this.restoreAddresses(select) })
  }

  // A bulk write reaches every campaign in whatever the page is filtered to. Filtered to one
  // user that is their own campaigns; filtered to a company it is everyone's, so name the
  // company and the count and make the person say yes.
  private confirmBulk(action: string): boolean {
    if (!this.confirmBulkValue) return true

    const campaigns = `${this.scopeCountValue} campaign${this.scopeCountValue === 1 ? "" : "s"}`

    return window.confirm(
      `${action} for all ${campaigns} at ${this.scopeLabelValue}? This covers every owner in the company.`
    )
  }

  private clearSelection(select: HTMLSelectElement): void {
    Array.from(select.options).forEach(option => { option.selected = false })
    select.dispatchEvent(new CustomEvent("manager-picker:sync", { bubbles: true }))
  }

  private submit(payload: Payload, element: HTMLInputElement | HTMLSelectElement): Promise<SaveResponse> {
    this.setBusy(element, true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

    return fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": csrfToken || "",
        "Accept": "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    })
      .then(response => response.json().then(data => {
        if (!response.ok || !data.success) throw data
        return data as SaveResponse
      }))
      .then(data => {
        this.toast(data.message || "Changes saved", false)
        return data
      })
      .catch(error => {
        this.toast(error?.message || "Failed to save changes", true)
        throw error
      })
      .finally(() => { this.setBusy(element, false) })
  }

  private rowToggle(row: HTMLElement): HTMLInputElement {
    return row.querySelector<HTMLInputElement>("[data-inbox-management-target='rowToggle']")!
  }

  private rowManagers(row: HTMLElement): HTMLSelectElement {
    return row.querySelector<HTMLSelectElement>("[data-inbox-management-target='rowManagers']")!
  }

  private rowAddresses(row: HTMLElement, target: string): HTMLSelectElement | null {
    return row.querySelector<HTMLSelectElement>(`[data-inbox-management-target='${target}']`)
  }

  private applyAddressDelta(row: HTMLElement, add: string[], remove: string[], target = "rowForwards"): void {
    const select = this.rowAddresses(row, target)
    if (!select) return

    add.forEach(address => { this.selectAddress(select, address) })
    Array.from(select.options).forEach(option => {
      if (remove.includes(option.value)) option.selected = false
    })

    this.storeAddresses(select, this.selectedValues(select))
  }

  private selectAddress(select: HTMLSelectElement, address: string): void {
    const existing = Array.from(select.options).find(option => option.value === address)
    if (existing) {
      existing.selected = true
      return
    }

    const option = document.createElement("option")
    option.value = address
    option.text = address
    option.selected = true
    select.appendChild(option)
  }

  private storedAddresses(select: HTMLSelectElement): string[] {
    try {
      return JSON.parse(select.dataset.previousValue || "[]")
    } catch {
      return []
    }
  }

  // Writing the attribute is also what tells email_chips_controller to repaint its chips.
  private storeAddresses(select: HTMLSelectElement, values: string[]): void {
    select.dataset.previousValue = JSON.stringify(values)
  }

  private restoreAddresses(select: HTMLSelectElement): void {
    const values = this.storedAddresses(select)
    Array.from(select.options).forEach(option => { option.selected = values.includes(option.value) })
    this.storeAddresses(select, values)
  }

  private picker(select: HTMLSelectElement): HTMLElement | null {
    return select.closest<HTMLElement>(".im-picker")
  }

  private setBusy(element: HTMLInputElement | HTMLSelectElement, busy: boolean): void {
    element.disabled = busy
    if (element.tagName === "SELECT") {
      this.picker(element as HTMLSelectElement)?.classList.toggle("is-saving", busy)
    }
  }

  private setRowManagersDisabled(row: HTMLElement, disabled: boolean): void {
    const select = this.rowManagers(row)
    select.disabled = disabled

    const picker = this.picker(select)
    if (!picker) return
    picker.classList.toggle("is-disabled", disabled)
    if (disabled) picker.classList.remove("open")
  }

  // The row's one-line summary restates what the switch and the picker say, so it
  // has to be re-derived from the DOM after every confirmed write.
  private updateRowStatus(row: HTMLElement): void {
    const status = row.querySelector<HTMLElement>("[data-inbox-management-target='rowStatus']")
    if (!status) return

    if (!this.rowToggle(row).checked) {
      status.textContent = `Not managed — replies stay with ${this.ownerValue}`
      return
    }

    const count = this.rowManagers(row).selectedOptions.length
    status.textContent = count === 0
      ? "Managed — follow-up reminders go to every admin"
      : `Managed by ${count} admin${count === 1 ? "" : "s"}`
  }

  // The master spans every page, so only the server can say which of the three states it is in --
  // the rows on screen are one page of the answer.
  private applyMasterState(data: SaveResponse): void {
    const state = data?.master_state
    if (state !== "all" && state !== "none" && state !== "mixed") return

    this.masterToggleTargets.forEach(master => {
      master.checked = state === "all"
      master.indeterminate = state === "mixed"
    })
  }

  private selectedValues(select: HTMLSelectElement): string[] {
    return Array.from(select.selectedOptions).map(option => option.value)
  }

  private toast(message: string, error: boolean): void {
    showToast(message, error ? "error" : "success")
  }
}
